import {Component, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DtoList} from "../../../../core/shared/dto/dto.list";
import {MvsCoreService} from "../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {ObjectRequestList} from "../../../../core/shared/dto/object/request/object-request-list";
import {ObjectIdentifier} from "../../../../core/shared/basic/object-identifier";
import {DtoDetail} from "../../../../core/shared/dto/dto.detail";

/**
 * TM-1000002003: Kommentare-Fixture (recording B) — isolated sandbox for the platform's generic
 * `mvs-object-comments` component on `xx.XxCarPilotBrand` / `xx.XxCarPilotBrandComment`.
 *
 * The page was previously a grab-bag test host (cc.ObjectType picker + mvs-search-entity demo);
 * that block was removed once the fixture became its sole purpose (the picker/search-entity demo
 * lived only here and in one unrelated console.log embedding on xx-table.page.html, which was
 * cleaned up alongside this).
 */
@Component({
    selector: 'mvs-xx-test-component',
    templateUrl: './test-component.component.html',
    standalone: false
})
export class XxTestComponentComponent implements OnInit {

    initialized: boolean; // indicator whether the component was initialized

    /** Name of the query param the chosen brand is carried in, so a reload/deep-link restores it. */
    private static readonly BRAND_ID_QUERY_PARAM = 'brandId';

    protected carPilotBrandService: MvsCrudService;

    /** All xx.XxCarPilotBrand rows, read through the framework (MvsCrudService), never search-index-backed. */
    carPilotBrands: DtoDetail[] = [];

    /** Currently chosen brand (bound to the p-select via ngModel). */
    selectedCarPilotBrand: DtoDetail = null;

    /**
     * The ObjectIdentifier mvs-object-comments mounts against (mainObject), same pattern as
     * cw-case-object-step-review.component.html's caseIdentifier.
     */
    carPilotBrandIdentifier: ObjectIdentifier = null;

    constructor(
        protected coreService: MvsCoreService,
        protected router: Router,
        protected route: ActivatedRoute) {
    }

    ngOnInit(): void {
        this.initComponent();
        this.refreshComponent();
        this.refreshCarPilotBrands();
    }

    /**
     * Initialize Component.
     */
    initComponent() {

        // TM-1000002003: Kommentare-Fixture — resolved through the registry (route-only entry),
        // never a hand-rolled HttpClient call.
        this.carPilotBrandService = <MvsCrudService>this.coreService.getCrudService("xx.XxCarPilotBrand");

    }

    /**
     * Refresh Component.
     */
    refreshComponent() {

        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["id"]) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {

    }

    /**
     * Loads every xx.XxCarPilotBrand row for the dropdown (framework CRUD read, no search index).
     * Once loaded, restores a brand previously carried in the route's `brandId` query param — this
     * is what makes a deep link (e.g. `#/xx/testComponent?brandId=2`) restore the fixture on reload
     * without requiring a click.
     */
    refreshCarPilotBrands() {
        const doListRequest = new ObjectRequestList(false, [], []);

        this.carPilotBrandService.list(doListRequest).subscribe(
            (res: DtoList) => {
                this.carPilotBrands = res?.entries ?? [];
                this.restoreCarPilotBrandFromRoute();
            }
        );
    }

    /**
     * Reads `brandId` from the current route's query params and pre-selects the matching brand.
     * An unknown or missing id leaves the selection empty — no error, no toast.
     */
    private restoreCarPilotBrandFromRoute() {
        const brandIdParam = this.route.snapshot.queryParamMap.get(XxTestComponentComponent.BRAND_ID_QUERY_PARAM);
        if (!brandIdParam) {
            return;
        }

        const brandId = Number(brandIdParam);
        const match = this.carPilotBrands.find(brand => brand.id === brandId);
        if (!match) {
            // unknown id -> nothing selected, no error
            return;
        }

        this.selectedCarPilotBrand = match;
        this.carPilotBrandIdentifier = new ObjectIdentifier('xx.XxCarPilotBrand', match.id);
    }

    /**
     * Handles a brand chosen in the p-select: builds the mainObject for mvs-object-comments and
     * carries the selection into the route as `?brandId=<id>` (relative to the current route,
     * merged with any other query params, hash routing preserved by the app's location strategy).
     */
    onCarPilotBrandSelect(brand: DtoDetail) {
        this.selectedCarPilotBrand = brand;
        this.carPilotBrandIdentifier = brand ? new ObjectIdentifier('xx.XxCarPilotBrand', brand.id) : null;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {[XxTestComponentComponent.BRAND_ID_QUERY_PARAM]: brand ? brand.id : null},
            queryParamsHandling: 'merge',
        });
    }

}
